import {
  Button,
  Modal,
  TextAreaInput,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { useValidations } from '../../../../../hooks/useValidations';
import { createRefund } from '../api';

const keyPrefix =
  'financialPortal.currentAccount.tabs.currentAccount.newRefund';

interface NewRefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: number;
  onSuccess: () => void;
}

interface FormShape {
  amount: string;
  concept: string;
}

const EMPTY_FORM: FormShape = {
  amount: '',
  concept: '',
};

export default function NewRefundModal({
  isOpen,
  onClose,
  studentId,
  onSuccess,
}: NewRefundModalProps) {
  const { t } = useTranslation();
  const { msgValidations, validateTextNotEmpty } = useValidations();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormShape>({
    defaultValues: EMPTY_FORM,
    mode: 'all',
  });

  const handleClose = useCallback(() => {
    reset(EMPTY_FORM);
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (values: FormShape) => {
      const amount = Number(values.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        addToast({
          text: t(`${keyPrefix}.errors.invalidAmount`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }
      const concept = values.concept.trim();
      if (!concept) {
        addToast({
          text: t(`${keyPrefix}.errors.emptyConcept`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      const { error } = await createRefund(studentId, { amount, concept });
      if (error) {
        addToast({
          text: t(`${keyPrefix}.errors.createFailed`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      addToast({
        text: t(`${keyPrefix}.success`),
        color: 'success',
        icon: 'success',
      });
      reset(EMPTY_FORM);
      onSuccess();
      onClose();
    },
    [studentId, t, reset, onSuccess, onClose],
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12}>
            <h3 className="mb-4">{t(`${keyPrefix}.title`)}</h3>
          </Col>
          <Col xs={12} className="mb-3">
            <TextInput
              name="amount"
              control={control}
              type={'number' as any}
              label={t(`${keyPrefix}.fields.amount.label`)}
              placeholder={t(`${keyPrefix}.fields.amount.placeholder`)}
              rules={{
                required: msgValidations.required,
                validate: (value: string) => {
                  const n = Number(value);
                  if (!Number.isFinite(n) || n <= 0) {
                    return t(`${keyPrefix}.errors.invalidAmount`) as string;
                  }
                  return undefined;
                },
              }}
            />
          </Col>
          <Col xs={12} className="mb-3">
            <TextAreaInput
              name="concept"
              control={control}
              label={t(`${keyPrefix}.fields.concept.label`)}
              placeholder={t(`${keyPrefix}.fields.concept.placeholder`)}
              rules={{
                required: msgValidations.required,
                validate: validateTextNotEmpty,
                maxLength: {
                  value: 500,
                  message: t(`${keyPrefix}.errors.maxLength`) as string,
                },
              }}
            />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col
            xs={12}
            className="d-flex justify-content-end"
            style={{ gap: 12 }}
          >
            <Button
              type="button"
              size="md"
              text={t(`${keyPrefix}.actions.cancel`)}
              outlined
              disabled={isSubmitting}
              onClick={handleClose}
            />
            <Button
              type="submit"
              size="md"
              text={t(`${keyPrefix}.actions.save`)}
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
